import React, { useEffect, useState } from "react";
import {
  Box,
  Stack,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  OutlinedInput,
  CircularProgress,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import {
  listAllHotelInoices,
  loadAllPages,
  resetAllHotelInvoices,
} from "../../../store/actions";
import { useSelector, useDispatch } from "react-redux";
import { useTheme } from "@mui/material/styles";
import { tokens } from "../../../theme";
import moment from "moment";
import CsvDownloader from "react-csv-downloader";
import DownloadIcon from "@mui/icons-material/Download";
import GetAppIcon from "@mui/icons-material/GetApp";

import { PaginationFooter, tableStyle } from "../../../components";

const formatUnixDate = (timestamp) => {
  if (!timestamp) return "N/A";
  return moment.unix(timestamp).format("DD MMM YYYY");
};

const getUniqueRoomNumbers = (room_charges = []) => {
  if (!room_charges.length) return "-";
  
  const validNumbers = room_charges
    .map((c) => c.roomNumber)
    .filter((num) => typeof num === "string" ? num.trim() !== "" : num != null);
  
  if (validNumbers.length === 0) return "-";
  
  const uniqueNumbers = [...new Set(validNumbers)];
  return uniqueNumbers.join(" | "); // ✅ Using pipe separator for CSV compatibility
};

const PAYMENT_STATUS_OPTIONS = [
  { value: "unpaid", label: "Unpaid" },
  { value: "partially_paid", label: "Partially Paid" },
  { value: "paid", label: "Paid" },
  { value: "credit", label: "Credit" },
];

const columns = [
  { field: "inv_no", headerName: "Invoice No.", flex: 1.2 },
  { field: "booking_id", headerName: "Booking ID", flex: 1.2 },
  {
    field: "room_number",
    headerName: "Room(s)",
    flex: 0.8,
    valueGetter: (params) => getUniqueRoomNumbers(params.row.room_charges),
  },
  {
    field: "inv_date",
    headerName: "Invoice Date",
    flex: 1,
    valueGetter: (params) => formatUnixDate(params.value),
  },
  {
    field: "amount",
    headerName: "Amount",
    flex: 0.75,
    type: "number",
    valueGetter: (params) => params.row.total || 0,
  },
  {
    field: "pmt_status",
    headerName: "Status",
    flex: 1,
    renderCell: (params) => {
      const statusColors = {
        paid: "success",
        unpaid: "error",
        partially_paid: "warning",
        credit: "info",
      };
      return (
        <Chip
          label={params.value?.replace("_", " ").toUpperCase()}
          color={statusColors[params.value] || "default"}
          size="small"
        />
      );
    },
  },
  {
    field: "checkin_date",
    headerName: "Check-in",
    flex: 1,
    valueGetter: (params) => formatUnixDate(params.value),
  },
  {
    field: "checkout_date",
    headerName: "Check-out",
    flex: 1,
    valueGetter: (params) => formatUnixDate(params.value),
  },
];

const BookingTab = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [pmtStatus, setPmtStatus] = useState([]);
  const [currentFilters, setCurrentFilters] = useState({});

  const { hotelInvoicesList, pageNumber, pageSize, count, isLoading, isLoadingMore } =
    useSelector(
      ({
        loading,
        admin: { hotelInvoice = {} } = {},
      }) => ({
        isLoading: loading,
        hotelInvoicesList: hotelInvoice.list || [],
        pageNumber: hotelInvoice.pageNumber || 1,
        pageSize: hotelInvoice.pageSize || 20,
        count: hotelInvoice.totalCount || 0,
        isLoadingMore: hotelInvoice.isLoadingMore || false,
      })
    );

  useEffect(() => {
    dispatch(resetAllHotelInvoices());
    const filters = buildFilters({ pageNumber: 1 });
    setCurrentFilters(filters);
    dispatch(listAllHotelInoices(filters, false));
  }, []);

  const buildFilters = (page) => {
    const filters = {
      page_number: page?.pageNumber || 1,
      page_size: page?.pageSize || pageSize,
    };

    if (startDate) {
      filters.start_date = moment(startDate, "YYYY-MM-DD").startOf("day").unix();
    }
    if (endDate) {
      filters.end_date = moment(endDate, "YYYY-MM-DD").endOf("day").unix();
    }
    if (pmtStatus.length > 0) {
      filters.pmt_status = pmtStatus;
    }

    return filters;
  };

  const handleFilter = () => {
    dispatch(resetAllHotelInvoices());
    const filters = buildFilters({ pageNumber: 1 });
    setCurrentFilters(filters);
    dispatch(listAllHotelInoices(filters, false));
  };

  const handlePageChange = (page) => {
    const filters = buildFilters(page);
    setCurrentFilters(filters);
    dispatch(listAllHotelInoices(filters, true));
  };

  const handleLoadAll = () => {
    const { page_number, page_size, ...baseFilters } = currentFilters;
    dispatch(loadAllPages(baseFilters, pageNumber, count));
  };

  const handleViewBooking = (invoice) => {
    navigate(`/billing/bookings/${invoice._id}`);
  };

  const prepareCSVData = () => {
    return hotelInvoicesList.map((invoice) => {
      const guestName = invoice.customer_id?.name || "N/A";
      const agentName = invoice.bill_to?.type === "AGENT" ? invoice.bill_to.name : "-";
      const roomNumbers = getUniqueRoomNumbers(invoice.room_charges);
      const BalanceAmount = (invoice.total || 0) - (invoice.paid_amount || 0);

      return {
        "Bill No": invoice.inv_no || "",
        "Bill Date": formatUnixDate(invoice.inv_date),
        "Room No": roomNumbers,
        "Check-in Date": formatUnixDate(invoice.checkin_date),
        "Check-out Date": formatUnixDate(invoice.checkout_date),
        "Guest Name": guestName,
        "Agent Name": agentName,
        "Amount": invoice.amount || 0,
        "SGST": invoice.sgst || 0,
        "CGST": invoice.cgst || 0,
        "Total": invoice.total || 0,
        "Paid Amount": invoice.paid_amount || 0,
        "Balance Amount": BalanceAmount,
        "Payment Status": invoice.pmt_status?.replace("_", " ").toUpperCase() || "",
        "Booking ID": invoice.booking_id || "",
      };
    });
  };

  const CustomFooter = () => (
    <PaginationFooter
      count={count}
      pageSize={pageSize}
      pageNumber={pageNumber}
      fetchMoreData={handlePageChange}
    />
  );

  const allDataLoaded = hotelInvoicesList.length >= count;
  const hasMoreToLoad = !allDataLoaded && count > pageSize;
  const remainingPages = Math.ceil((count - hotelInvoicesList.length) / pageSize);

  return (
    <Box m="20px">
      <Stack direction="row" spacing={1} my={2} alignItems="center" flexWrap="wrap">
        <TextField
          label="Start Date"
          type="date"
          size="small"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{
            minWidth: 160,
            '& input[type="date"]::-webkit-calendar-picker-indicator': {
              filter: theme.palette.mode === "dark" ? "invert(1)" : "invert(0)",
            },
          }}
        />
        <TextField
          label="End Date"
          type="date"
          size="small"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          InputProps={{ inputProps: { min: startDate } }}
          sx={{
            minWidth: 160,
            '& input[type="date"]::-webkit-calendar-picker-indicator': {
              filter: theme.palette.mode === "dark" ? "invert(1)" : "invert(0)",
            },
          }}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Payment Status</InputLabel>
          <Select
            multiple
            value={pmtStatus}
            onChange={(e) => setPmtStatus(e.target.value)}
            input={<OutlinedInput label="Payment Status" />}
            renderValue={(selected) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip
                    key={value}
                    label={value.replace("_", " ").toUpperCase()}
                    size="small"
                  />
                ))}
              </Box>
            )}
          >
            {PAYMENT_STATUS_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="contained" onClick={handleFilter}>
          Search
        </Button>

        {hasMoreToLoad && (
          <Button
            variant="outlined"
            startIcon={isLoadingMore ? <CircularProgress size={16} /> : <GetAppIcon />}
            onClick={handleLoadAll}
            disabled={isLoadingMore}
          >
            {isLoadingMore 
              ? `Loading... (${hotelInvoicesList.length}/${count})`
              : `Load All (${remainingPages} more pages)`
            }
          </Button>
        )}

        <CsvDownloader
          datas={prepareCSVData()}
          filename={`invoices_${moment().format("YYYY-MM-DD_HHmmss")}`}
          extension=".csv"
          separator=","
          wrapColumnChar=""
        >
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            disabled={hotelInvoicesList.length === 0 || isLoadingMore}
          >
            Export CSV ({hotelInvoicesList.length})
          </Button>
        </CsvDownloader>
      </Stack>

      {isLoadingMore && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            Loading all invoices... {hotelInvoicesList.length} of {count} loaded
          </Typography>
        </Box>
      )}

      <Box sx={{ height: "68vh", width: "100%", ...tableStyle(colors) }}>
        <DataGrid
          loading={isLoading}
          rows={hotelInvoicesList}
          columns={columns}
          components={{ Footer: CustomFooter }}
          getRowId={(row) => row._id}
          onRowClick={(params) => handleViewBooking(params.row)}
          sx={{ "& .MuiDataGrid-row:hover": { cursor: "pointer" } }}
        />
      </Box>
    </Box>
  );
};

export default BookingTab;